import { IInteractionCapture } from '../../domain/interfaces/IInteractionCapture';
import { LogEntry } from '../../domain/types';

export class InteractionCapture implements IInteractionCapture {
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;
  private lastInputTime = 0;
  private inputDebounceMs = 500;

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    
    this.setupListeners();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.removeListeners();
    this.callback = null;
    this.isActive = false;
  }

  private setupListeners(): void {
    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('submit', this.handleSubmit, true);
    document.addEventListener('change', this.handleChange, true);
    document.addEventListener('input', this.handleInput, true);
  }

  private removeListeners(): void {
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('submit', this.handleSubmit, true);
    document.removeEventListener('change', this.handleChange, true);
    document.removeEventListener('input', this.handleInput, true);
  }

  private getCssSelector(element: Element): string {
    const path: string[] = [];
    let current: Element | null = element;

    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase();
      
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break;
      } else if (current.className) {
        const classes = Array.from(current.classList).join('.');
        if (classes) {
          selector += `.${classes}`;
        }
      }
      
      const siblings = current.parentElement?.children;
      if (siblings && siblings.length > 1) {
        const index = Array.from(siblings).indexOf(current) + 1;
        selector += `:nth-child(${index})`;
      }
      
      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(' > ');
  }

  private handleClick = (event: MouseEvent): void => {
    if (!this.callback || !event.target) return;
    
    const target = event.target as Element;
    const selector = this.getCssSelector(target);
    const text = (target as HTMLElement).innerText?.substring(0, 50);

    this.callback({
      level: 'info',
      msg: `Click: ${selector}`,
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'interaction',
        event: 'click',
        target: selector,
        value: text,
        url: window.location.href
      }
    });
  };

  private handleSubmit = (event: Event): void => {
    if (!this.callback || !event.target) return;
    
    const form = event.target as HTMLFormElement;
    const selector = this.getCssSelector(form);
    const action = form.action || window.location.href;
    const method = form.method || 'GET';

    this.callback({
      level: 'info',
      msg: `Form submit: ${selector}`,
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'interaction',
        event: 'submit',
        target: selector,
        value: `${method} ${action}`,
        url: window.location.href
      }
    });
  };

  private handleChange = (event: Event): void => {
    if (!this.callback || !event.target) return;
    
    const element = event.target as HTMLInputElement | HTMLSelectElement;
    const selector = this.getCssSelector(element);
    const value = element.type === 'password' ? '[redacted]' : element.value?.substring(0, 50);

    this.callback({
      level: 'info',
      msg: `Change: ${selector}`,
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'interaction',
        event: 'change',
        target: selector,
        value,
        url: window.location.href
      }
    });
  };

  private handleInput = (event: Event): void => {
    if (!this.callback || !event.target) return;
    
    // Debounce input events
    const now = Date.now();
    if (now - this.lastInputTime < this.inputDebounceMs) return;
    this.lastInputTime = now;
    
    const element = event.target as HTMLInputElement | HTMLTextAreaElement;
    const selector = this.getCssSelector(element);
    const value = element.type === 'password' ? '[redacted]' : `${element.value.length} chars`;

    this.callback({
      level: 'info',
      msg: `Input: ${selector}`,
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'interaction',
        event: 'input',
        target: selector,
        value,
        url: window.location.href
      }
    });
  };
}